/**
 * friendlyError — translate raw network/HTTP error strings into copy that's
 * safe to show a CEO. Avoids leaking endpoints, IPs, stack frames, or low-level
 * Node/Postgres error codes to the live UI.
 *
 * Use anywhere a hook returns an `error` string from a fetch/timeout pipeline.
 */
export function friendlyError(raw) {
  if (!raw) return "Something went wrong. Try again in a moment.";
  const s = String(raw).toLowerCase();

  // Auth
  if (s.includes("401") || s.includes("authentication"))
    return "Your session expired. Please sign in again.";
  if (s.includes("403") || s.includes("forbidden"))
    return "You don't have permission to view this. Ask the admin to grant access.";

  // Rate limit
  if (s.includes("429") || s.includes("rate limit") || s.includes("too many"))
    return "We're catching up — please retry in a few seconds.";

  // Timeout / network
  if (s.includes("timeout"))
    return "The request took too long. Try a narrower date range or retry.";
  if (
    s.includes("failed to fetch") ||
    s.includes("network") ||
    s.includes("econnrefused") ||
    s.includes("err_connection") ||
    s.includes("err_network") ||
    s.includes("net::")
  )
    return "Couldn't reach the server. Check your connection and retry.";

  // Backend 5xx
  if (s.includes("500") || s.includes("502") || s.includes("503") || s.includes("504") || s.includes("internal"))
    return "Backend is briefly unavailable. Retry in a moment.";

  // Postgres / SQL leakage
  if (s.includes("relation") || s.includes("column") || s.includes("syntax") || s.includes("postgres") || s.includes("pg "))
    return "We hit a data issue on this view. Tell the engineering team and try again.";

  // Malformed payload
  if (s.includes("malformed") || s.includes("envelope") || s.includes("unexpected"))
    return "Got an unexpected response. Retry the request.";

  // Default: short generic message — never echo the raw string back.
  return "Couldn't load this data. Retry, or pick a different date range.";
}

/**
 * Classify an error string into a kind the UI can theme around. Lets the
 * empty-state component pick a friendly title + visual that matches WHAT
 * actually went wrong, instead of the same red "Something went wrong" for
 * every case.
 *
 *   timeout  → backend's slow but alive — we just waited too long
 *   network  → fetch never reached the server
 *   server   → server returned 5xx
 *   auth     → 401/403 (handled separately by login redirect, but classify anyway)
 *   data     → malformed payload, schema/SQL leakage
 *   unknown  → anything else
 */
export function errorKind(raw) {
  if (!raw) return "unknown";
  const s = String(raw).toLowerCase();
  if (s.includes("401") || s.includes("403") || s.includes("authentication") || s.includes("forbidden")) return "auth";
  if (s.includes("timeout")) return "timeout";
  if (s.includes("failed to fetch") || s.includes("network") || s.includes("econnrefused") ||
      s.includes("err_connection") || s.includes("err_network") || s.includes("net::")) return "network";
  if (s.includes("500") || s.includes("502") || s.includes("503") || s.includes("504") || s.includes("internal")) return "server";
  if (s.includes("malformed") || s.includes("envelope") || s.includes("unexpected") ||
      s.includes("relation") || s.includes("column") || s.includes("syntax") ||
      s.includes("postgres") || s.includes("pg ")) return "data";
  return "unknown";
}
